import { useCallback, useEffect, useRef, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { captureFrontendError } from 'utils/captureFrontendError'
import { fetchIrrigationData } from './_utils/fetchIrrigationData'
import { fetchNdviData } from './_utils/fetchNdviData'
import { fetchSoilData } from './_utils/fetchSoilData'
import type { TIcsDashboardData } from './types'

type TLoadingKey = 'ndvi' | 'ytd' | 'irrigation' | 'soil'

export const useIcsDashboardData = () => {
  const [data, setData] = useState<Partial<TIcsDashboardData> | null>(null)

  const [loadingStates, setLoadingStates] = useState<Record<TLoadingKey, boolean>>({
    ndvi: false,
    ytd: false,
    irrigation: false,
    soil: false,
  })

  const [errorStates, setErrorStates] = useState<Record<TLoadingKey, Error | null>>({
    ndvi: null,
    ytd: null,
    irrigation: null,
    soil: null,
  })

  // Incremented on refetch to reset comment textareas via React key
  const [resetKey, setResetKey] = useState(0)
  const fetchIdRef = useRef(0)
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const selectedProperty = fieldAssetStore.useSelector((s) => s.properties)?.[Number(selectedPropertyId)]
  const hasSelectedPropertyTimezone = !!selectedProperty?.timezone

  const fetchIrrigation = useCallback(
    async (fetchId: number) => {
      if (!selectedProperty?.timezone) return

      setLoadingStates((prev) => ({ ...prev, irrigation: true, ytd: true }))

      setErrorStates((prev) => ({ ...prev, irrigation: null, ytd: null }))

      try {
        const { irrigationData, fetchYtdData } = await fetchIrrigationData(
          selectedProperty,
          selectedProperty.timezone,
        )

        if (fetchIdRef.current !== fetchId) return

        setData((prev) => ({ ...prev, ...irrigationData }))

        setLoadingStates((prev) => ({ ...prev, irrigation: false }))

        try {
          const ytd = await fetchYtdData()

          if (fetchIdRef.current !== fetchId) return

          setData((prev) => ({ ...prev, ytd }))
        } catch (err) {
          if (fetchIdRef.current !== fetchId) return

          const error = err instanceof Error ? err : new Error('Failed to fetch YTD data')

          captureFrontendError(error)

          setErrorStates((prev) => ({ ...prev, ytd: error }))
        } finally {
          if (fetchIdRef.current === fetchId) {
            setLoadingStates((prev) => ({ ...prev, ytd: false }))

            setResetKey((prev) => prev + 1)
          }
        }
      } catch (err) {
        if (fetchIdRef.current !== fetchId) return

        const error = err instanceof Error ? err : new Error('Failed to fetch irrigation data')

        captureFrontendError(error)

        setErrorStates((prev) => ({ ...prev, irrigation: error, ytd: error }))

        setLoadingStates((prev) => ({ ...prev, irrigation: false, ytd: false }))
      }
    },
    [selectedProperty],
  )

  const fetchNdvi = useCallback(
    async (fetchId: number) => {
      const blockIds = Object.keys(selectedProperty?.blocks ?? {}).map(Number)

      setLoadingStates((prev) => ({ ...prev, ndvi: true }))

      setErrorStates((prev) => ({ ...prev, ndvi: null }))

      try {
        const ndvi = await fetchNdviData(blockIds)

        if (fetchIdRef.current !== fetchId) return

        setData((prev) => ({ ...prev, ndvi }))
      } catch (err) {
        if (fetchIdRef.current !== fetchId) return

        const error = err instanceof Error ? err : new Error('Failed to fetch NDVI data')

        captureFrontendError(error)

        setErrorStates((prev) => ({ ...prev, ndvi: error }))
      } finally {
        if (fetchIdRef.current === fetchId) {
          setLoadingStates((prev) => ({ ...prev, ndvi: false }))
        }
      }
    },
    [selectedProperty],
  )

  const fetchSoil = useCallback(
    async (fetchId: number) => {
      if (!selectedProperty?.timezone) return

      setLoadingStates((prev) => ({ ...prev, soil: true }))

      setErrorStates((prev) => ({ ...prev, soil: null }))

      try {
        const soilResult = await fetchSoilData(selectedProperty, selectedProperty.timezone)

        if (fetchIdRef.current !== fetchId) return

        setData((prev) => ({ ...prev, soil: soilResult }))
      } catch (err) {
        if (fetchIdRef.current !== fetchId) return

        const error = err instanceof Error ? err : new Error('Failed to fetch soil data')

        captureFrontendError(error)

        setErrorStates((prev) => ({ ...prev, soil: error }))
      } finally {
        if (fetchIdRef.current === fetchId) {
          setLoadingStates((prev) => ({ ...prev, soil: false }))
        }
      }
    },
    [selectedProperty],
  )

  useEffect(() => {
    if (selectedPropertyId) {
      setData(null)

      setErrorStates({ ndvi: null, ytd: null, irrigation: null, soil: null })

      const fetchId = ++fetchIdRef.current

      if (!selectedProperty?.timezone) {
        const irrigationError = new Error('Failed to fetch irrigation data')
        const ytdError = new Error('Failed to fetch YTD data')
        const soilError = new Error('Failed to fetch soil data')

        setErrorStates({ ndvi: null, ytd: ytdError, irrigation: irrigationError, soil: soilError })

        setLoadingStates({ ndvi: false, ytd: false, irrigation: false, soil: false })

        return
      }

      fetchIrrigation(fetchId)

      fetchNdvi(fetchId)

      fetchSoil(fetchId)
    }
  }, [selectedPropertyId, hasSelectedPropertyTimezone, fetchIrrigation, fetchNdvi, fetchSoil])

  return {
    data,
    loadingStates,
    errorStates,
    refetchIrrigation: () => fetchIrrigation(++fetchIdRef.current),
    resetKey,
  }
}
